import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import './CheckoutPage.css';

/* global Tyro */

export default function PayPage() {
  const [searchParams] = useSearchParams();
  const paySecret = searchParams.get('s');
  const payRequestId = searchParams.get('r');
  const orderIdsStr = searchParams.get('o');
  const totalStr = searchParams.get('t');
  const orderNumber = searchParams.get('n');
  const orderGroup = searchParams.get('g');

  const orderIds = orderIdsStr ? orderIdsStr.split(',').map(Number) : [];
  const total = parseFloat(totalStr) || 0;

  const [error, setError] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tyroConfig, setTyroConfig] = useState({ liveMode: false, googlePayMerchantId: '' });
  const tyroRef = useRef(null);
  const payFormRef = useRef(null);
  const [formReady, setFormReady] = useState(false);

  useEffect(() => {
    api.getTyroConfig().then(setTyroConfig).catch(() => {});
  }, []);

  const formRefCallback = useCallback((node) => {
    if (node) setFormReady(true);
    else setFormReady(false);
  }, []);

  useEffect(() => {
    if (!formReady || !paySecret || !window.Tyro) return;

    (async () => {
      try {
        const tyro = Tyro({ liveMode: tyroConfig.liveMode });
        tyroRef.current = tyro;
        await tyro.init(paySecret);

        const googlePayOptions = { enabled: true, merchantInfo: { merchantName: 'St Barbara Kitchen' } };
        // Production merchant ID only attaches when Tyro is live. See
        // CheckoutPage.js for the full rationale (OR_BIBED_11 avoidance).
        if (tyroConfig.liveMode && tyroConfig.googlePayMerchantId) {
          googlePayOptions.merchantInfo.merchantId = tyroConfig.googlePayMerchantId;
        }
        console.info('Tyro pay form config:', {
          liveMode: tyroConfig.liveMode,
          googlePayMerchantIdSet: !!googlePayOptions.merchantInfo.merchantId,
        });

        const payForm = tyro.createPayForm({
          options: {
            applePay: { enabled: true, totalLabel: 'St Barbara Kitchen' },
            googlePay: googlePayOptions,
            creditCardForm: { enabled: true },
          },
        });

        payFormRef.current = payForm;
        await payForm.inject('#tyro-pay-form');
      } catch (err) {
        console.error('Pay page Tyro init error:', err);
        setError('Failed to load payment form. Please try again.');
      }
    })();
  }, [formReady, paySecret, tyroConfig.liveMode]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!tyroRef.current) return;

    setPaymentProcessing(true);
    setError(null);

    try {
      await tyroRef.current.submitPay();
      const payRequest = await tyroRef.current.fetchPayRequest();

      if (payRequest.status === 'SUCCESS') {
        await api.confirmPayment(orderIds, paySecret, payRequestId);
        setSuccess(true);
      } else {
        const txn = payRequest.transactionResults?.[0];
        const detail = txn ? `${txn.result || ''} ${txn.gatewayCode || ''} ${txn.errorMessage || ''}`.trim() : '';
        setError(`Payment ${payRequest.status}: ${detail || 'Please try again.'}`);
      }
    } catch (err) {
      console.error('Pay page submitPay error:', err);
      setError(`Payment error: ${err?.errorMessage || err?.message || 'Please try again.'}`);
    } finally {
      setPaymentProcessing(false);
    }
  }

  if (!paySecret) {
    return (
      <main className="checkout-page container">
        <h2 className="checkout-title">Payment</h2>
        <p className="error-text">Invalid payment link. Please return to the app and try again.</p>
      </main>
    );
  }

  if (success) {
    const confirmPath = orderGroup
      ? `/confirmation/group/${orderGroup}`
      : orderNumber
        ? `/confirmation/${orderNumber}`
        : '/';
    return (
      <main className="checkout-page container">
        <h2 className="checkout-title">Payment Successful</h2>
        <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={{ fontSize: '1.2rem', color: '#2d6a4f' }}>Your payment has been processed.</p>
          <p>You can close this window and return to the app, or tap below to view your order.</p>
          <a href={confirmPath} className="btn btn-primary" style={{ marginTop: '1rem', display: 'inline-block' }}>
            View Order
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="checkout-page container">
      <h2 className="checkout-title">Complete Payment</h2>

      <div className="checkout-summary card">
        <h3>Total: ${total.toFixed(2)}</h3>
      </div>

      <form onSubmit={handleSubmit} className="tyro-card-form card">
        <div id="tyro-pay-form" ref={formRefCallback} className="tyro-pay-form-container" />

        {paymentProcessing && <p className="wallet-processing">Processing payment...</p>}
        {error && <p className="error-text">{error}</p>}

        <button
          type="submit"
          className="btn btn-primary btn-block checkout-submit"
          disabled={paymentProcessing}
        >
          {paymentProcessing ? 'Processing...' : `Pay $${total.toFixed(2)}`}
        </button>
      </form>
    </main>
  );
}
